import { type FC, lazy, Suspense, useState, useRef, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _notNil, _isNotEmpty, _isEmptyString, _isNil, _isNotBlank } from '@/littledash';

import type { SampleTypeRow } from '@/model/SampleType.model';
import type { ValidationData } from '@/utils/sampleTypeValidation';

import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { successToast, errorToast } from '@/helpers';
import { ApiService } from '@/support/ApiService.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { featuresSelector } from '@/support/Selectors';
import Loading from '@/components/Loading';

/**
 * Lazy load both table versions to ensure complete isolation between Handsontable v12 and v16.
 * V12 and V16 register different cell types, validators, and editors globally in Handsontable.
 * Static imports would cause both versions to initialize their modules on page load,
 * leading to conflicts where V16 validators/editors could affect V12 tables and vice versa.
 * With lazy loading, only the required version's modules are loaded based on the `hotUpgrade` flag,
 * preventing cross-version interference.
 */
const BulkAddSampleTypesTableV12 = lazy(() => import('./BulkAddSampleTypesTableV12'));
const BulkAddSampleTypesTableV16 = lazy(() => import('./BulkAddSampleTypesTableV16'));

const AVAILABLE_DETAIL_OPTIONS: Array<string> = [
  'Weight (mg g µg)',
  'Volume (ml µl cm³ mm³)',
  'Width (mm)',
  'Length (mm)',
  'Depth (mm)',
];

const DISPLAY_TO_API_MAP: Record<string, string> = {
  'Weight (mg g µg)': 'weight',
  'Volume (ml µl cm³ mm³)': 'volume',
  'Width (mm)': 'width',
  'Length (mm)': 'length',
  'Depth (mm)': 'depth',
};

interface HotTableRefLike {
  hotInstance?: {
    getData?: () => Array<SampleTypeRow>;
  } | null;
}

interface BulkAddSampleTypesProps {
  onSave: () => void;
  onCancel: () => void;
}

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
}));

const BulkAddSampleTypes: FC<BulkAddSampleTypesProps> = ({ onSave, onCancel }) => {
  const { hotUpgrade } = useSelector(hotUpgradeSelector);
  const [saving, setSaving] = useState(false);
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [loadingValidation, setLoadingValidation] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const hotTableRef = useRef<HotTableRefLike | null>(null);

  useEffect(() => {
    const fetchValidationData = async (): Promise<void> => {
      try {
        const response = await ApiService.call({
          endpoint: 'GET /api/v1/team-glossary/sample-validation-data',
          path: undefined,
          options: {
            onError: { toast: false, throw: true, capture: false, slug: 'bulk-add-sample-types-validation-load' },
          },
        });
        setValidationData(response.body as ValidationData);
      } catch (error) {
        errorToast('Failed to load validation data');
        ExceptionHandler.captureException(
          new InVivoError('Failed to load validation data', {
            cause: error,
            slug: 'bulk-add-sample-types-validation-load',
          })
        );
        setValidationData(null);
      } finally {
        setLoadingValidation(false);
      }
    };

    void fetchValidationData();
  }, []);

  const initialData: Array<SampleTypeRow | []> = useMemo(() => {
    return [['', '', ''], []];
  }, []);

  const handleSave = async () => {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (_isNil(hotInstance)) {
      errorToast('Unable to access table data');
      return;
    }

    setSaving(true);

    try {
      const currentData = (hotInstance.getData?.() ?? []) as Array<SampleTypeRow>;

      const validRows = currentData.filter((row) => {
        const [title] = row;
        return _isNotBlank(title);
      });

      if (validRows.length === 0) {
        errorToast('Please add at least one sample type');
        setSaving(false);
        return;
      }

      const teamGlossariesPayload = validRows.map((row) => {
        const [title, label, detailsRaw] = row;

        const detailsDisplay = Array.isArray(detailsRaw)
          ? detailsRaw.map((d) => String(d).trim()).filter(Boolean)
          : detailsRaw
            ? String(detailsRaw)
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean)
            : [];

        const detailsApi = detailsDisplay
          .map((d) => DISPLAY_TO_API_MAP[d])
          .filter(
            (d): d is 'volume' | 'weight' | 'width' | 'length' | 'depth' =>
              d !== undefined && ['volume', 'weight', 'width', 'length', 'depth'].includes(d)
          );

        const maybeTitle = (_notNil(title) ? title : '').toString().trim();
        const maybeLabel = (_notNil(label) ? label : '').toString().trim();

        return {
          title: maybeTitle,
          type: 'none' as const,
          description: null,
          group: 'samples' as const,
          options: _isNotEmpty(detailsApi) ? { details: detailsApi } : null,
          ...(_notNil(maybeLabel) && !_isEmptyString(maybeLabel) && { label: maybeLabel }),
        };
      });

      await ApiService.call({
        endpoint: 'POST /api/v1/team-glossary',
        path: undefined,
        body: { team_glossaries: teamGlossariesPayload },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-add-sample-types-save' } },
      });

      successToast(`Successfully added ${validRows.length} sample type${validRows.length !== 1 ? 's' : ''}`);
      onSave();
    } catch (error) {
      errorToast('Failed to create sample types');
      ExceptionHandler.captureException(
        new InVivoError('Failed to create sample types', {
          cause: error,
          slug: 'bulk-add-sample-types-save',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalContainer className="bg-white">
      <ModalHeader title="Create" closeModal={onCancel} closeDisabled={saving} className="pa3 bb b--moon-gray w-100" />

      <div className="pa3">
        {loadingValidation ? (
          <div className="tc pa4">
            <div className="mb2">Loading validation data...</div>
          </div>
        ) : (
          <Suspense fallback={<Loading />}>
            {hotUpgrade ? (
              <BulkAddSampleTypesTableV16
                data={initialData}
                innerRef={hotTableRef}
                validationData={validationData}
                availableDetailOptions={AVAILABLE_DETAIL_OPTIONS}
                setInvalid={setHasValidationErrors}
              />
            ) : (
              <BulkAddSampleTypesTableV12
                data={initialData}
                innerRef={hotTableRef}
                validationData={validationData}
                availableDetailOptions={AVAILABLE_DETAIL_OPTIONS}
                setInvalid={setHasValidationErrors}
              />
            )}
          </Suspense>
        )}
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={onCancel}
        submitBtnText="Create"
        cancelBtnText="Cancel"
        submitButtonProps={{
          loading: saving,
          disabled: saving || loadingValidation || hasValidationErrors,
        }}
        cancelButtonProps={{
          disabled: saving,
        }}
      />
    </ModalContainer>
  );
};

export default BulkAddSampleTypes;
